import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceAnimatable() {
  const sectionStyle = {
    padding: "1rem",
    paddingLeft: "35px",
    borderRadius: "12px",
    marginBottom: "2rem",
  };

  const animatableProperties = [
    "aspect-ratio",
    "background",
    "background-color",
    "background-position",
    "background-position-x",
    "background-position-y",
    "background-size",
    "block-size",
    "border",
    "border-bottom",
    "border-bottom-color",
    "border-end-end-radius",
    "border-end-start-radius",
    "border-block",
    "border-block-color",
    "border-block-end-color",
    "border-block-end-width",
    "border-block-start-color",
    "border-block-start-width",
    "border-block-width",
    "border-bottom-left-radius",
    "border-bottom-right-radius",
    "border-inline",
    "border-inline-color",
    "border-inline-end-color",
    "border-inline-end-width",
    "border-inline-start-color",
    "border-inline-start-width",
    "border-inline-width",
    "border-start-end-radius",
    "border-start-start-radius",
    "border-bottom-width",
    "border-color",
    "border-left",
    "border-left-color",
    "border-left-width",
    "border-right",
    "border-right-color",
    "border-right-width",
    "border-spacing",
    "border-top",
    "border-top-color",
    "border-top-left-radius",
    "border-top-right-radius",
    "border-top-width",
    "bottom",
    "box-shadow",
    "clip",
    "color",
    "column-count",
    "column-gap",
    "column-rule",
    "column-rule-color",
    "column-rule-width",
    "column-width",
    "columns",
    "filter",
    "flex",
    "flex-basis",
    "flex-grow",
    "flex-shrink",
    "font",
    "font-size",
    "font-size-adjust",
    "font-stretch",
    "font-weight",
    "grid",
    "grid-area",
    "grid-auto-columns",
    "grid-auto-flow",
    "grid-auto-rows",
    "grid-column",
    "grid-column-end",
    "grid-column-gap",
    "grid-column-start",
    "grid-gap",
    "grid-row",
    "grid-row-end",
    "grid-row-gap",
    "grid-row-start",
    "grid-template",
    "grid-template-areas",
    "grid-template-columns",
    "grid-template-rows",
    "height",
    "inline-size",
    "inset",
    "inset-block",
    "inset-block-end",
    "inset-block-start",
    "inset-inline",
    "inset-inline-end",
    "inset-inline-start",
    "left",
    "letter-spacing",
    "line-height",
    "margin",
    "margin-block",
    "margin-block-end",
    "margin-block-start",
    "margin-bottom",
    "margin-inline",
    "margin-inline-end",
    "margin-inline-start",
    "margin-left",
    "margin-right",
    "margin-top",
    "max-height",
    "max-width",
    "max-block-size",
    "max-inline-size",
    "min-block-size",
    "min-inline-size",
    "min-height",
    "min-width",
    "object-position",
    "offset-anchor",
    "offset-distance",
    "offset-path",
    "offset-rotate",
    "opacity",
    "order",
    "outline",
    "outline-color",
    "outline-offset",
    "outline-width",
    "padding",
    "padding-block",
    "padding-block-end",
    "padding-block-start",
    "padding-bottom",
    "padding-inline",
    "padding-inline-end",
    "padding-inline-start",
    "padding-left",
    "padding-right",
    "padding-top",
    "perspective",
    "perspective-origin",
    "right",
    "rotate",
    "scale",
    "text-decoration-color",
    "text-indent",
    "text-shadow",
    "top",
    "transform",
    "transform-origin",
    "translate",
    "vertical-align",
    "visibility",
    "width",
    "word-spacing",
    "z-index",
  ];

  return (
    <div className="tutorial-container">
      {/* Title & Intro */}
      <h1 className="tutorial-title">🎨 CSS Animatable Properties</h1>
      <p className="tutorial-intro">
        These are CSS properties that can be animated using{" "}
        <code>transition</code> or <code>@keyframes</code>.
      </p>

      {/* Key Points */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fce7f3, #fbcfe8)",
          color: "#4a044e",
        }}
      >
        <h2>🔑 Key Points</h2>
        <ul>
          <li>Animatable properties can smoothly transition between values.</li>
          <li>
            Supports CSS <code>transition</code> and <code>@keyframes</code>{" "}
            animations.
          </li>
          <li>Not all properties are animatable (check browser support).</li>
          <li>Use these properties to create visually engaging effects.</li>
        </ul>
      </div>

      {/* Properties Table */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #e0f7fa, #b2ebf2)",
          color: "#006064",
          overflowX: "auto",
        }}
      >
        <h2>📝 Animatable Properties List</h2>
        <table
          className="tutorial-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#006064",
                color: "#ffffff",
                textAlign: "left",
              }}
            >
              <th style={{ padding: "12px" }}>Property Name</th>
            </tr>
          </thead>
          <tbody>
            {animatableProperties.map((prop, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#b2ebf2" : "#e0f7fa",
                  transition: "all 0.3s ease",
                  cursor: "default",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#80deea")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "#b2ebf2" : "#e0f7fa")
                }
              >
                <td style={{ padding: "12px", fontWeight: "bold" }}>{prop}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Example */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #dbeafe, #e0f2fe)",
          color: "#1e3a8a",
        }}
      >
        <h2>🧠 Example</h2>
        <CodeCopyBlock
          language="css"
          code={`/* Example: Animate background-color and transform */
.box {
  width: 100px;
  height: 100px;
  background-color: #4a90e2;
  transition: background-color 0.5s ease, transform 0.5s ease;
}

.box:hover {
  background-color: #e94e77;
  transform: scale(1.2);
}`}
        />
        <p>
          Hover over the box to see the animation of background color and scale.
        </p>
      </div>

      {/* Next Steps */}
      <div
        style={{
          ...sectionStyle,
          background: "linear-gradient(90deg, #fef9c3, #fef08a)",
          color: "#713f12",
        }}
      >
        <h2>🚀 Next Steps</h2>
        <ul>
          <li>
            Try animating different properties using <code>transition</code>.
          </li>
          <li>
            Create complex animations using <code>@keyframes</code>.
          </li>
          <li>Check browser compatibility for each animatable property.</li>
        </ul>
      </div>
    </div>
  );
}
